// Every tunable number in one place, so PLAN §6 can sweep them in Phase 5
// without hunting through the codebase. Each carries the reasoning from the
// plan; if you change one, change the reasoning too.

import {
  CONFESSOR,
  DAM,
  DREADNOUGHT,
  ALICORN,
  GRYPHON,
  JOAN_OF_ARC,
  JOAN_QUEEN,
  MARSHAL,
  MATRIARCH,
  MIRACLE_HEALER,
  PALADIN,
  PEGASUS,
  TURBO_BISHOP,
  TURBO_ROOK,
  UNICORN,
  VARIANT_PLAIN
} from "src/shared/variants";
import {
  BISHOP,
  CLASS_MONARCH,
  DIRECTIONS,
  KING,
  KNIGHT,
  NORTH,
  PAWN,
  type PieceClass,
  type PieceType,
  QUEEN,
  ROOK,
  stepOffset
} from "./board";

export type Grade = 0 | 1 | 2 | 3 | 4;

/**
 * The metal line's rungs, grade-indexed (§26 F2). Material is **lines** now
 * (metal, stone, special, fuel — see `materials.ts`), and this is the metal
 * ladder's names: three rungs (§31 M1). Grades 3–4 are retired slots — the
 * nibble stays five wide because `units-<slot>` ids are wire format.
 *
 * Kept under this name because almost everything that renders a grade is
 * rendering metal — the only line with a source until deposits (F3) land.
 * Anything that can meet a stone or special unit reads `materialName` instead.
 *
 * Grade 0 is still what a capture leaves on the ground; it is only *called*
 * iron once it has been picked up and is being carried as a unit.
 */
export const GRADE_NAMES: readonly string[] = ["Iron", "Steel", "Crucible Steel"];

// `MAX_GRADE` is vocabulary (`src/shared/industry.ts`), re-exported so no
// reader moved.
import { FUEL, MAX_GRADE, METAL, SPECIAL, STONE, type MaterialLine } from "src/shared/industry";
export { MAX_GRADE };

/**
 * Chess value: what a capture yields and what the planner weighs. **Never a
 * price** — a forge reads `FORGE_BILLS` (§31 M4).
 */
export const PIECE_VALUE: Record<PieceType, number> = {
  [PAWN]: 1,
  [KNIGHT]: 3,
  [BISHOP]: 3,
  [ROOK]: 5,
  [QUEEN]: 9,
  [KING]: 0
};

/**
 * What a king is worth when its army *surrenders* rather than dies.
 *
 * `PIECE_VALUE[KING]` is zero because a king is never captured, so in ordinary
 * play the number never comes up. It does at a stage boundary, where a beaten
 * army lays down its arms and collapses into scrap — and there the king was
 * the one piece that converted to nothing at all, which at the moment of
 * victory is conspicuously the wrong piece to leave no trace.
 *
 * Priced at a queen: it is the piece the whole formation was built around, and
 * a surrendered army was not destroyed in the fighting, so there is more of it
 * left than a fought-over corpse would leave.
 */
export const SURRENDER_VALUE: Record<PieceType, number> = {
  ...PIECE_VALUE,
  [KING]: PIECE_VALUE[QUEEN]
};

/**
 * The design switches (§31.1 Q1, Q4): the lead designer's rulings that the
 * lead engineer asked to keep reversible. **A constant, not a flag** — the
 * rules may not read a flag, and two clients must derive one game from one log.
 *
 *  - `bishopRefines` — does a plain bishop cook (`isRefinery`)? Yes (Q1).
 *  - `captureDamage` — what every capture deals, armor-piercing: a number, or
 *    `null` for the per-rank `PIECE_DAMAGE`. Five (Q4); a doubler
 *    (`blowMultiplier`) deals ten.
 *  - `metalNeedsFuel` — does raising metal a rung cost a fuel point (§31 M2)?
 *    Yes. Off is the pre-M2 economy: every refiner cooks metal for free.
 */
export const DESIGN: {
  readonly bishopRefines: boolean;
  readonly captureDamage: number | null;
  readonly metalNeedsFuel: boolean;
} = {
  bishopRefines: true,
  captureDamage: 5,
  metalNeedsFuel: true
};

/**
 * How much health each rank has (§31 K1), and how hard its blow lands (§27).
 *
 * **Their own tables, deliberately not `PIECE_VALUE`** — that number is what a
 * piece is worth *in a trade* (mint bill, corpse, tally); tie toughness to it
 * and no combat number can be tuned without moving the economy.
 *
 * **The tables are the dial.** Chess is preserved exactly when every ordinary
 * blow is lethal:
 *
 * ```
 * min(CAPTURE_DAMAGE[a]) >= max(PIECE_HEALTH[b], variant health)
 * ```
 *
 * At the design's numbers (flat 5 against a top of 5) it holds, so the blow
 * fires only against pieces `vitality` toughens — and the coming Dragons.
 * `regime.test.ts` pins the regime, because a silent change is a game that
 * quietly became a different game.
 *
 * `[KING]` is a **spare** king's. A *last* king's nought is a carve-out in
 * `maxHealthOf` — a rule about the game ending, not a fact about the rank.
 */
export const PIECE_HEALTH: Record<PieceType, number> = {
  [PAWN]: 2,
  [KNIGHT]: 3,
  [BISHOP]: 2,
  [ROOK]: 3,
  [QUEEN]: 2,
  [KING]: 5
};

/**
 * What a rank's blow takes off when `DESIGN.captureDamage` is `null`. The
 * §27 drafts, kept as the switch's other position. Read through `blowOf` /
 * `CAPTURE_DAMAGE`, never directly.
 */
export const PIECE_DAMAGE: Record<PieceType, number> = {
  [PAWN]: 1,
  [KNIGHT]: 3,
  [BISHOP]: 3,
  [ROOK]: 5,
  [QUEEN]: 9,
  [KING]: 9
};

/** What a rank's plain blow takes off, after the switch: the table `blowOf` reads. */
export const CAPTURE_DAMAGE: Record<PieceType, number> = {
  [PAWN]: DESIGN.captureDamage ?? PIECE_DAMAGE[PAWN],
  [KNIGHT]: DESIGN.captureDamage ?? PIECE_DAMAGE[KNIGHT],
  [BISHOP]: DESIGN.captureDamage ?? PIECE_DAMAGE[BISHOP],
  [ROOK]: DESIGN.captureDamage ?? PIECE_DAMAGE[ROOK],
  [QUEEN]: DESIGN.captureDamage ?? PIECE_DAMAGE[QUEEN],
  [KING]: DESIGN.captureDamage ?? PIECE_DAMAGE[KING]
};

/**
 * Base armor (`Position.plating`) a rank is born with (§31 K1): a knight 2, a
 * rook 10. Granted where a piece is named (`armNewborn`), so every creation
 * site — FEN, mint, wave, scenario — gets it without being told. Ranged damage
 * eats it; a capture bypasses it.
 */
export const PIECE_PLATING: Record<PieceType, number> = {
  [PAWN]: 0,
  [KNIGHT]: 2,
  [BISHOP]: 0,
  [ROOK]: 10,
  [QUEEN]: 0,
  [KING]: 0
};

/** The six ranks. */
const ORDINARY: readonly PieceType[] = [PAWN, KNIGHT, BISHOP, ROOK, QUEEN, KING];

/**
 * Would every ordinary blow be lethal — i.e. does the game still reduce to
 * chess? See `PIECE_HEALTH`. Read by `regime.test.ts` and by nothing in the
 * game, which is the point: it is a claim about the tables, not a mode.
 */
export const chessSafeTables = (
  damage: Record<PieceType, number> = CAPTURE_DAMAGE,
  health: Record<PieceType, number> = PIECE_HEALTH
): boolean =>
  Math.min(...ORDINARY.map((t) => damage[t])) >=
  Math.max(...ORDINARY.map((t) => health[t]));

/**
 * How much harder one cleared front makes the run.
 *
 * **One number for the whole ramp**, because a run has several counters keyed
 * to the front number — how heavy a camp may be, how much rank its pieces have
 * past the end of the table, when the opponent stops playing easy, when a
 * front may open on two sides — and moving them one at a time is how a curve
 * comes to mean different things at different points on it. Every one of them
 * reads `pacedStage` and none of them reads the front number directly.
 *
 * At **0.7** a front is worth 0.7 of a step, so the run reaches any given
 * difficulty about 40% further in: hard from the sixth front rather than the
 * fourth, two fronts at once from the eleventh rather than the eighth. The
 * *front* counter is untouched — the chip, the save, the score and every
 * seeded stream still count fronts — because what was too fast was the climb
 * and not the counting.
 *
 * The first front is exactly the first front at any pace (`pacedStage(1)` is
 * 1), or a slower ramp would quietly change the one board every run opens on.
 */
export const STAGE_PACE = 0.7;

/** One row of a recipe: so many units of `line` at `grade` (or better, on a ladder). */
export interface ForgeRow {
  readonly line: MaterialLine;
  readonly grade: number;
  readonly count: number;
}

/**
 * **What a forge costs, by rank — the design doc's table, verbatim** (§31 M4).
 * Replaced `mintBill = 2 × PIECE_VALUE`; nothing prices a mint off chess value.
 *
 * The **metal row comes first** in every bill — `mintCost` and `mintGradeOf`
 * read it. A ladder row (metal, stone) is paid at its rung or above, lowest
 * qualifying rung first; a resource row (electrum, diamond) is that resource
 * and nothing else (`unitMeets`), so the king's two special rows never count
 * one unit twice. Every count still exceeds `PIECE_VALUE` (Sacrifice loses).
 */
export const FORGE_BILLS: Record<PieceType, readonly ForgeRow[]> = {
  [PAWN]: [{ line: METAL, grade: 0, count: 2 }],
  [KNIGHT]: [{ line: METAL, grade: 1, count: 6 }],
  [BISHOP]: [{ line: METAL, grade: 1, count: 9 }],
  [ROOK]: [{ line: METAL, grade: 1, count: 12 }],
  [QUEEN]: [
    { line: METAL, grade: 2, count: 14 },
    { line: SPECIAL, grade: 0, count: 3 },
    { line: SPECIAL, grade: 1, count: 1 }
  ],
  [KING]: [
    { line: METAL, grade: 2, count: 6 },
    { line: SPECIAL, grade: 0, count: 5 },
    { line: SPECIAL, grade: 1, count: 3 }
  ]
};

/**
 * Units of the bill's **metal row** — the scalar the planner, the badges and
 * variant drafts read. Off `FORGE_BILLS`, never off `PIECE_VALUE`.
 */
export const mintCost = (type: PieceType): number => FORGE_BILLS[type][0].count;

/** Every unit a bill asks for, across its rows. */
export const billUnits = (type: PieceType): number =>
  FORGE_BILLS[type].reduce((sum, row) => sum + row.count, 0);

// **Grade still gates the tier you can mint; quantity is still the cost.**
// `mintGradeOf(type)` in `materials.ts` is the metal row's rung. The deadlock
// argument is unchanged: the king refines, so a refinery is always on the
// board — and since §31 M2 a refiner also needs coal to raise metal.

// **Piece variants**: a rank the board already has, under a different name and
// with different numbers. The type field is three bits, so a variant cannot be
// a `PieceType`. It is a descriptor keyed by square (`Position.variant`), and the
// piece keeps its rank. `PIECE_CLASS` is keyed by rank, so a row's class,
// promotion, facing and combat tables all follow with nothing written down.
//
// A row earns a behavioural field and its reader one at a time; every reader
// sits behind a `variantAt` that answers plain, so an unspecialised board takes
// the old path. **`range` is the exception** — see `VARIANT_MAX_RANGE`.

/**
 * A step a variant may take, **relative to its facing**: `[forward, right]`.
 *
 * Facing-relative because a table in board terms needs one row per facing and a
 * rule keeping the four in step. `VARIANT_OFFSETS` precomputes all four.
 */
export type VariantStep = readonly [forward: number, right: number];

/**
 * The eight neighbours as facing-relative steps, clockwise from forward.
 *
 * One ordered table read twice — the DAM's movement, where order is irrelevant,
 * and its mining scan, where order *is* the rule.
 */
export const CLOCKWISE_STEPS: readonly VariantStep[] = [
  [1, 0],
  [1, 1],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [-1, -1],
  [0, -1],
  [1, -1]
];

/**
 * The eight (3,2) leaps — a knight's move with a square added to each leg.
 *
 * Written facing-relative like every other row table and resolved **at north
 * alone**: the set is closed under the quarter turn, so all four facings give
 * the same eight offsets and a facing index would be four copies of one row.
 * `variants.test.ts` pins that rather than trusting it.
 */
export const ZEBRA_STEPS: readonly VariantStep[] = [
  [3, 2],
  [3, -2],
  [2, 3],
  [2, -3],
  [-2, 3],
  [-2, -3],
  [-3, 2],
  [-3, -2]
];

/**
 * How a variant moves, in three sets — "may move there" and "may take there"
 * come apart, as the pawn already proves.
 *
 * A step over one square is a **slide, not a leap**: every square between must
 * be empty. One rule rather than a per-row flag.
 */
export interface VariantMoves {
  /** Steps it both moves to and captures on. */
  steps: readonly VariantStep[];
  /** Steps it moves to but never captures on. */
  quiet: readonly VariantStep[];
  /** Steps it captures on but never moves to — the pawn's diagonal. */
  attacks: readonly VariantStep[];
}

export interface PieceVariant {
  /** What it is called. */
  name: string;
  /** Short form, for a badge with no room for "Drone-Assisted Miner". */
  short: string;
  /**
   * The rank it is a variant of. **Every other rule reads this, not the row** —
   * which is the whole of why adding one changes no existing behaviour.
   */
  base: PieceType;
  /**
   * What the row costs (§31 P3, the design doc's table): rows like a forge
   * bill's, paid by the upgrade's source — a forge-capable piece beside it or
   * an Armory within 2 (`industry/upgrades.ts`) — plus `RULES.upgradeFuel`.
   * Empty for the plain row.
   */
  bill: readonly ForgeRow[];
  /**
   * What a whole one takes to put down, or absent for the base rank's row.
   *
   * Read by `maxHealthOf`, beside `vitality`:
   * health decides whether a blow is a capture, which decides which moves
   * exist. **Not `vitality`**, which is a per-square toughness a card grants —
   * one number for both could not be tuned apart.
   */
  health?: number;
  /**
   * The row's **whole** armor, in place of its rank's `PIECE_PLATING` (§31
   * K1: a Turbo-Rook has 3 "instead of 10"). Taking the row moves the piece's
   * plate and its cap by the difference (`reframePlating`); absent keeps the
   * rank's. A capture bypasses plating, so this is toughness against fire alone.
   */
  plating?: number;
  /**
   * What its blow is multiplied by, read by `blowOf`. Absent is 1.
   *
   * **A multiplier where every other row field is an override**, because the
   * claim is "double", not "eighteen": `PIECE_DAMAGE` is a draft nobody has
   * balanced, and an absolute would stop being double the day it is tuned.
   */
  blowMultiplier?: number;
  /** How it moves, or absent to move exactly as its base rank does. */
  moves?: VariantMoves;
  /**
   * Steps it may take **on top of** its rank's own pattern, and never capture
   * on — facing-relative, one square, `moves`' `quiet` in the other direction.
   *
   * `moves` *replaces* a rank's generator, which is right for a row that is a
   * different piece (a Marshal, a drone) and wrong for one that is its rank
   * plus something (a Confessor is a bishop that can also shuffle). Two fields
   * rather than a flag, because they are two questions.
   *
   * **Quiet only, and that is a rule rather than an omission.** A capturing
   * step has to be taught to `attacks.ts` — `VARIANT_OFFSETS.takes` is what the
   * sweep reads — so a row wanting one is a change to argue for there, not a
   * longer list here.
   */
  extraQuiet?: readonly VariantStep[];
  /**
   * Leaps it may take **on top of** its rank's own pattern, moving or taking —
   * the Pegasus Knight's (3,2) beside the knight's own (2,1).
   *
   * `extraQuiet`'s opposite number, and the reason that field is quiet-only: a
   * step that captures has to be taught to `attacks.ts`, and this one is.
   * `VARIANT_LEAP_ORIGINS` says where such a piece could stand and the row
   * itself answers whether it really reaches, so this table is the one
   * definition of it — the sweep never spells the geometry out a second time.
   *
   * **Resolved at north alone**, unlike every other row table: the sets are
   * closed under the quarter turn. **No promotion is expanded** for a leap —
   * no leaping row is a pawn. Both are pinned in `variants.test.ts`.
   */
  leaps?: readonly VariantStep[];
  /**
   * May it spend its move to hand the piece beside it `RULES.empowerMoves`?
   *
   * See the `empower` command. On a cooldown of `RULES.empowerRounds`, kept in
   * `IndustryState.preachedAt` — a per-piece clock that movegen never reads and `migratePiece`
   * carries.
   */
  empowers?: boolean;
  /**
   * Does it spend its factory tick mending the pieces around it, refining only
   * when there is nobody to mend? `industry/healers.ts` is the rest.
   *
   * A flag and not a rate, `empowers`' reason: how much belongs in `RULES`.
   */
  heals?: boolean;
  /**
   * Does it carry a horn — the `lance` command, and the picket around it?
   *
   * **One flag for both halves**, the Marshal's reason. Both are damage rather
   * than capture, so `attacks.ts` never sees either — `rules/unicorn.ts`.
   */
  lances?: boolean;
  /**
   * May it spend its move turning `RULES.rallyPieces` of its own fodder?
   *
   * **Not `commandsFodder`**, which also hands each of them a second miner's
   * turn — a Paladin gives orders, not overtime. See the `rally` command.
   */
  rallies?: boolean;
  /**
   * How fast it works a heap, or absent for `HARVEST_RATE[base]`.
   *
   * Optional rather than defaulted because **zero is a real answer** — the
   * Marshal does not dig, and `??` would read a stored 0 as "no override".
   */
  harvest?: number;
  /**
   * Commands the fodder beside it: `drill` turns them, and each gets a second
   * *miner's turn* a round.
   *
   * **Not a §26 F4 tick** — that advances `turnCounter` and reruns the whole
   * factory; this rewinds two stamps on one square. They compose, so commanded
   * fodder digs twice per bought tick.
   *
   * One flag for both, or a row could drill pieces it does not work with.
   */
  commandsFodder?: boolean;
  /**
   * Digs a square it is not standing on — its own first, then clockwise from
   * the one it faces. `miningTargetAt` is the whole of it.
   *
   * No `harvest` beside it deliberately: the grant is tempo (no move spent
   * relocating), not throughput.
   */
  minesAround?: boolean;
  /**
   * How far it slides, overriding `SLIDER_RANGE[base]`.
   *
   * **The one row field the attack sweep cannot ignore** — a piece reaching
   * past the sweep's bound is never found, so check detection goes blind past
   * it. See `VARIANT_MAX_RANGE` and `docs/claude/armies.md`.
   */
  range?: number;
  /**
   * Refines by the row, whatever its rank does (`refinesAt`) — the Confessor
   * and the Miracle Healer keep their ovens if `DESIGN.bishopRefines` is off.
   */
  refines?: boolean;
  /**
   * Industrially nothing — no oven, no hopper, no delivery.
   *
   * Wider than "cannot refine" on purpose, and filed in `machineType` because
   * that is what four other predicates read off.
   */
  noMachine?: boolean;
  /**
   * A foundry, whatever its rank is (`isFoundryAt`, `capacityAt`).
   *
   * `noMachine`'s opposite and deliberately *not* a `machineType` answer: that
   * function reports a piece **kind**, and reporting a queen as a rook would
   * hand her a rook's every other answer too. Being a foundry is already
   * composed on top of it, so this is read where the crown was.
   */
  foundry?: boolean;
  /**
   * The class this row is, overriding its rank's `PIECE_CLASS`.
   *
   * Read through `pieceClassAt`, never `pieceClass` — the rank table takes a
   * bare piece and cannot see a row. §26.1 (10)'s three Monarch fences are what
   * a `CLASS_MONARCH` row actually buys.
   */
  pieceClass?: PieceClass;
  /**
   * What grants this row, for the rows `upgrade` may not sell.
   *
   * Present means the reducer refuses the purchase and the inspector draws the row
   * switched off saying this — the turret's shape: a card
   * you have not drawn is something a player can go and get, and a row that
   * appears from nowhere the turn it lands is met by accident.
   */
  grantedBy?: string;
  /** Only `tallySide(state.run)` may take this row. Reducer gate, inspector omission. */
  playerOnly?: boolean;
  /**
   * A **fuse** (§31 P4): the bodies that become this row, the first the
   * **anchor** — the one that stays, keeping its square, id, wounds, banks and
   * gear; the rest are consumed. Present means `upgrade` never sells it
   * (`variantsFor` omits it) and only `fuse` makes one (`campaign/fusion.ts`).
   * `base` is what the anchor becomes (a Dreadnought's rook turns queen).
   */
  fusedFrom?: readonly FusePart[];
  /** What its horn deals, for `RULES.lanceDamage` (§31 P4: the Alicorn's 7). */
  lanceDamage?: number;
  /**
   * May its `lance` ride one of its own moves — "jump and shoot in the same
   * move" (§31 P4)? The shot is taken from where the move lands.
   */
  lancesOnMove?: boolean;
}

/** One body a fuse takes: a rank, and the row it must wear (plain for most). */
export interface FusePart {
  type: PieceType;
  variant: number;
}

// The variant ids are `src/shared/variants.ts` — a row here, a model key in
// the client — re-exported here.
export {
  CONFESSOR,
  DAM,
  DREADNOUGHT,
  ALICORN,
  GRYPHON,
  JOAN_OF_ARC,
  JOAN_QUEEN,
  MARSHAL,
  MATRIARCH,
  MIRACLE_HEALER,
  PALADIN,
  PEGASUS,
  TURBO_BISHOP,
  TURBO_ROOK,
  UNICORN,
  VARIANT_PLAIN
};

/**
 * What a turbo piece reaches. Well past `RULES.sliderRange` (10) on purpose —
 * on the classic board it is simply "the whole board", which is the intent.
 */
export const TURBO_RANGE = 30;

/**
 * What a Joan-Pattern Queen reaches — still past a slider's ten, and on the
 * classic board still the whole diagonal.
 */
export const JOAN_RANGE = 12;

/**
 * What a Confessor reaches — **shorter than her rank**, and the first row that
 * is. See `variantRange`: reach overrides rather than raises for this one.
 */
export const CONFESSOR_RANGE = 7;

/**
 * What a Miracle Healer reaches. Seven like the Confessor, and its own constant:
 * two rows agreeing today is not one row's reach being defined as another's.
 */
export const HEALER_RANGE = 7;

/**
 * A Unicorn Knight's horn, a **Chebyshev** radius — the turret's measure.
 *
 * **Deliberately not `range`**, the field the sweep cannot ignore: this reach
 * only ever hurts, so `VARIANT_MAX_RANGE` is rightly unmoved.
 */
export const UNICORN_REACH = 2;

/** A Dreadnought's reach (§31 P4): the queen's shape, seven squares of it. */
export const DREADNOUGHT_RANGE = 7;

/**
 * What a Alicorn's horn deals (§31 P4), moved or not — the
 * Unicorn's reach (`UNICORN_REACH`), seven where the Unicorn's is
 * `RULES.lanceDamage`. Damage, never a capture, so the sweep never sees it.
 */
export const EQUINOID_LANCE = 7;

/**
 * The knight's eight (2,1) leaps as a row table — the Alicorn's, a king
 * that is not a knight by rank. Closed under the quarter turn, like `ZEBRA_STEPS`.
 */
export const KNIGHT_STEPS: readonly VariantStep[] = [
  [2, 1],
  [2, -1],
  [1, 2],
  [1, -2],
  [-1, 2],
  [-1, -2],
  [-2, 1],
  [-2, -1]
];

/** The four orthogonals as facing-relative steps. Symmetric, so facing is moot. */
// An upgrade bill's rows (§31 P3). Ladder rows take any rung at or above.
const steel = (count: number): ForgeRow => ({ line: METAL, grade: 1, count });
const crucible = (count: number): ForgeRow => ({ line: METAL, grade: 2, count });
const stone = (count: number): ForgeRow => ({ line: STONE, grade: 1, count });
const marble = (count: number): ForgeRow => ({ line: STONE, grade: 2, count });
const electrum = (count: number): ForgeRow => ({ line: SPECIAL, grade: 0, count });
const diamond = (count: number): ForgeRow => ({ line: SPECIAL, grade: 1, count });

const ORTHOGONAL_STEPS: readonly VariantStep[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1]
];

export const PIECE_VARIANTS: readonly PieceVariant[] = [
  { name: "Pawn", short: "Pawn", base: PAWN, bill: [] },
  {
    name: "Marshal",
    short: "Marshal",
    base: PAWN,
    bill: [steel(4)],
    health: 4,
    plating: 2,
    // **A sergeant, not a soldier**: it stops digging and makes the fodder
    // around it dig twice, which is worth more than its own hands were.
    harvest: 0,
    commandsFodder: true,
    /**
     * Orthogonal in three directions and diagonal only to take.
     *
     * The forward *capture* is the sharp end and the real departure from a
     * pawn: a pawn can be blocked by a piece it cannot touch, and a Marshal
     * takes it. The two-forward is a push it may make any turn rather than a
     * home-rank double step, so it sets no en passant square — see
     * `generateVariantMoves`. Nothing goes backwards.
     */
    moves: {
      steps: [
        [1, 0],
        [0, 1],
        [0, -1]
      ],
      quiet: [[2, 0]],
      attacks: [
        [1, 1],
        [1, -1]
      ]
    }
  },
  {
    name: "Drone-Assisted Miner",
    short: "DAM",
    base: PAWN,
    bill: [electrum(1), stone(3)],
    // Its own square, then the eight around it. See `minesAround`.
    minesAround: true,
    // **All eight in `quiet`, both take-lists empty.** That is the whole of
    // "cannot capture" — `walkTo` refuses an occupied square — and it is why
    // `takes` is empty, so the sweep finds it covers nothing.
    moves: {
      steps: [],
      quiet: CLOCKWISE_STEPS,
      attacks: []
    }
  },
  // The turbo pair: a rank's own shape, further, minus its factory role.
  // No `moves` table — a slider variant is a number, not a move generator.
  {
    name: "Turbo-Bishop",
    short: "T-Bishop",
    base: BISHOP,
    bill: [crucible(5)],
    range: TURBO_RANGE,
    noMachine: true,
    playerOnly: true
  },
  {
    name: "Turbo-Rook",
    short: "T-Rook",
    base: ROOK,
    bill: [crucible(5)],
    range: TURBO_RANGE,
    noMachine: true,
    // Granted once, and a capture bypasses it — toughness against turret fire.
    plating: 3,
    playerOnly: true
  },
  {
    /**
     * The queen's shape, twelve squares of it, hitting twice as hard (ten),
     * with four health and three armor (§31 K1).
     *
     * `noMachine` is the turbo pair's price in the same words: the queen's
     * factory role is a collector's pocket and the Signet Crown's foundry, and
     * she gives up both — nothing routes to her, nothing hands to her, and the
     * crown is refused outright. Her *rank* still sweeps, since `vacuumAlong`
     * reads the rank.
     */
    name: "Joan-Pattern Queen",
    short: "Joan",
    base: QUEEN,
    bill: [crucible(5), electrum(1), marble(1)],
    health: 4,
    plating: 3,
    range: JOAN_RANGE,
    blowMultiplier: 2,
    noMachine: true
  },
  {
    /**
     * A queen in every number — she is her rank's shape, reach, health and
     * blow, and the row says nothing about any of them. What it says is what
     * she *is*: royalty, and a foundry.
     *
     * **`upgrade` never sells it** (`grantedBy`): the Signet Crown is the one
     * door, and the card pays this bill at an upgrade site (§31 C5, P3).
     */
    name: "Matriarch",
    short: "Matriarch",
    base: QUEEN,
    bill: [electrum(3), diamond(3)],
    grantedBy: "the Signet Crown",
    foundry: true,
    // A queen who is royalty, and the first Monarch that is not a `KING`. She
    // is still not a *king* — check, mate and `royalsOf` are the crown's, not
    // the class's — so what this buys is §26.1 (10)'s three exemptions.
    pieceClass: CLASS_MONARCH
  },
  {
    /**
     * A bishop who gave up reach for weight and for somebody else's tempo.
     *
     * Seven squares of diagonal instead of ten, twice the blow, five health,
     * and a shuffle: one orthogonal square that cannot take, which is how she
     * gets *off* her colour — the one thing no bishop has ever been able to do.
     *
     * She keeps her oven (`isRefinery` is keyed by rank and nothing here says
     * `noMachine`), so unlike the turbo pair she pays for none of it with the
     * factory. What she pays with is reach and a turn every other round.
     */
    name: "Confessor",
    short: "Confessor",
    base: BISHOP,
    bill: [crucible(5), electrum(1), marble(1)],
    health: 5,
    range: CONFESSOR_RANGE,
    blowMultiplier: 2,
    extraQuiet: ORTHOGONAL_STEPS,
    empowers: true,
    // Refines by her row, whatever `DESIGN.bishopRefines` says (§31 M3).
    refines: true
  },
  {
    name: "Miracle Healer",
    short: "Healer",
    base: BISHOP,
    bill: [crucible(7), diamond(1)],
    // Three health (§31 K1): a capture's five finishes her outright.
    health: 3,
    range: HEALER_RANGE,
    // The Confessor's shuffle — a bishop that can reach the piece it wants to
    // stand beside without waiting for the right colour of square.
    extraQuiet: ORTHOGONAL_STEPS,
    heals: true,
    refines: true
  },
  {
    /**
     * A knight with a second ring: the eight (2,1) leaps it always had, and
     * eight (3,2) ones outside them, chosen freely per move. Sixteen squares
     * where a knight has eight, and the same hole in the middle — it still
     * cannot move one square, or two straight, or onto its own diagonal.
     *
     * **The row says nothing else, so it is a knight in every other number** —
     * its blow, its health, its repair reserve, its re-aim reach and its
     * machine role are all the rank's. It is the only row that is purely its
     * rank plus something, which is what `leaps` is for.
     */
    name: "Pegasus Knight",
    short: "Pegasus",
    base: KNIGHT,
    bill: [crucible(4)],
    leaps: ZEBRA_STEPS
  },
  {
    /**
     * A knight that fights at arm's length: `lance` for a turn, and a picket
     * that costs none.
     *
     * **The row says nothing about movement**, so it is a knight in every
     * ordinary number. The horn is damage and never a capture, which is why a
     * mechanic this reachy moves no move.
     */
    name: "Unicorn Knight",
    short: "Unicorn",
    base: KNIGHT,
    bill: [crucible(6)],
    lances: true
  },
  {
    /**
     * Two diagonal, then one orthogonal from there.
     *
     * **That route lands on the knight's own eight *and* the zebra's eight**,
     * so this row reaches exactly what a Pegasus does — (2,2) steps to (1,2)
     * and (2,1), which are L moves, and to (3,2) and (2,3), which are not.
     * `gryphonRoute` in `variants.test.ts` derives it rather than trusting it.
     *
     * Shipped as a twin knowingly (designer's call): it is a second identity
     * and price on one geometry, not a second mechanic.
     */
    name: "Gryphon Knight",
    short: "Gryphon",
    base: KNIGHT,
    bill: [crucible(4)],
    // The outer ring only — `leaps` adds to the rank, which already has the L.
    leaps: ZEBRA_STEPS
  },
  {
    /**
     * A knight in plate: the L, the eight squares beside it, and orders.
     *
     * **The neighbours are `leaps` and not `extraQuiet`** — it *takes* on them,
     * and a capturing step has to be seen by `attacks.ts`. `leaps` is the field
     * that is, so a Paladin gives check from a square it is touching. At one
     * square a leap and a step are the same thing, there being nothing between.
     */
    name: "Paladin",
    short: "Paladin",
    base: KNIGHT,
    bill: [crucible(5), diamond(1), marble(1)],
    // Granted once, and a capture bypasses it — toughness against fire alone.
    plating: 5,
    leaps: CLOCKWISE_STEPS,
    rallies: true
  },
  // --- the fuses (§31 P4): made by `fuse`, never sold by `upgrade` ---------
  {
    /**
     * Bishop and Rook: the queen's eight lines at seven, five health and
     * fifteen armor, refining and forging. **Anchored on the rook**, which
     * turns queen — the rank whose lines it moves on; `refines` and
     * `foundry` say the rest by the row, whatever `DESIGN.bishopRefines` does.
     */
    name: "Dreadnought",
    short: "Dreadnought",
    base: QUEEN,
    bill: [crucible(2)],
    health: 5,
    plating: 15,
    range: DREADNOUGHT_RANGE,
    refines: true,
    foundry: true,
    fusedFrom: [
      { type: ROOK, variant: VARIANT_PLAIN },
      { type: BISHOP, variant: VARIANT_PLAIN }
    ]
  },
  {
    /**
     * Pegasus, Unicorn and King: the king's step, the knight's (2,1) and the
     * Pegasus' (3,2) — **both rings as `leaps`**, since its rank is a king's
     * and only `leaps` reaches `attacks.ts` — five health, five armor, and the
     * horn at seven on any of its moves (`lancesOnMove`). **Anchored on the
     * king**, so it stays the royal, the Monarch and the gear's bearer.
     */
    name: "Alicorn",
    short: "Alicorn",
    base: KING,
    bill: [diamond(5)],
    health: 5,
    plating: 5,
    leaps: [...KNIGHT_STEPS, ...ZEBRA_STEPS],
    lances: true,
    lanceDamage: EQUINOID_LANCE,
    lancesOnMove: true,
    fusedFrom: [
      { type: KING, variant: VARIANT_PLAIN },
      { type: KNIGHT, variant: PEGASUS },
      { type: KNIGHT, variant: UNICORN }
    ]
  },
  {
    /**
     * Joan-Pattern Queen and Matriarch: Joan's numbers (four health, three
     * armor, twelve squares, the double blow) with the oven back and the
     * Matriarch's foundry and crown. **Anchored on the Matriarch**, the
     * Monarch, so her id, trophy and gear survive.
     */
    name: "Joan of Arc",
    short: "Joan of Arc",
    base: QUEEN,
    bill: [electrum(3)],
    health: 4,
    plating: 3,
    range: JOAN_RANGE,
    blowMultiplier: 2,
    refines: true,
    foundry: true,
    pieceClass: CLASS_MONARCH,
    fusedFrom: [
      { type: QUEEN, variant: MATRIARCH },
      { type: QUEEN, variant: JOAN_QUEEN }
    ]
  }
];

/** The fuse rows (§31 P4), in table order. */
export const FUSE_ROWS: readonly number[] = PIECE_VARIANTS.map((_row, id) => id).filter(
  (id) => PIECE_VARIANTS[id].fusedFrom !== undefined
);

/**
 * Every row's steps resolved to board offsets, indexed `[variant][facing]`.
 *
 * Precomputed for `PAWN_CAPTURES_BY_FACING`'s reason — the attack sweep runs
 * this per square per query. `takes` is the union the sweep wants, so no caller
 * concatenates two lists per square.
 */
export const VARIANT_OFFSETS: readonly (readonly {
  readonly steps: readonly number[];
  readonly quiet: readonly number[];
  readonly attacks: readonly number[];
  readonly takes: readonly number[];
}[])[] = PIECE_VARIANTS.map((row) =>
  DIRECTIONS.map((facing) => {
    const resolve = (list: readonly VariantStep[] = []) =>
      list.map(([forward, right]) => stepOffset(facing, forward, right));
    const steps = resolve(row.moves?.steps);
    const attacks = resolve(row.moves?.attacks);
    return { steps, quiet: resolve(row.moves?.quiet), attacks, takes: [...steps, ...attacks] };
  })
);

/**
 * Every row's `extraQuiet` resolved to board offsets, indexed `[variant][facing]`.
 *
 * Its own table and not part of `VARIANT_OFFSETS`, for `SCAN_OFFSETS`' reason:
 * that one answers "what does this row *take* on", which the attack sweep reads,
 * and these steps take nothing. Folding them in would make a Confessor's shuffle
 * look like a threat.
 */
export const VARIANT_EXTRA_OFFSETS: readonly (readonly (readonly number[])[])[] =
  PIECE_VARIANTS.map((row) =>
    DIRECTIONS.map((facing) =>
      (row.extraQuiet ?? []).map(([forward, right]) => stepOffset(facing, forward, right))
    )
  );

/**
 * Every row's `leaps` resolved to board offsets, indexed by variant.
 *
 * **One row of offsets, not four**: `ZEBRA_STEPS` is closed under the quarter
 * turn, so north's resolution is every facing's. A row whose leaps were not
 * would need a facing index here, and `variants.test.ts` is what would notice.
 */
export const VARIANT_LEAP_OFFSETS: readonly (readonly number[])[] = PIECE_VARIANTS.map((row) =>
  (row.leaps ?? []).map(([forward, right]) => stepOffset(NORTH, forward, right))
);

/**
 * Every square a leaping row could reach a square *from*, as offsets from the
 * square under attack.
 *
 * Derived, `VARIANT_MAX_RANGE`'s rule and for its reason: the attack sweep
 * radiates outward from the target, so a ring it does not walk is a piece
 * check detection cannot see. **Negated rather than reused** — the shipped
 * sets are symmetric, so the two are the same table today, and a row that was
 * not would otherwise be swept backwards with nothing throwing.
 */
export const VARIANT_LEAP_ORIGINS: readonly number[] = [
  ...new Set(VARIANT_LEAP_OFFSETS.flat().map((offset) => -offset))
];

/**
 * `CLOCKWISE_STEPS` resolved to board offsets, indexed by facing.
 *
 * Its own table and not `VARIANT_OFFSETS`: that is what a *row* may do, this is
 * the neighbourhood, so narrowing a row's movement must not narrow its reach.
 */
export const SCAN_OFFSETS: readonly (readonly number[])[] = DIRECTIONS.map((facing) =>
  CLOCKWISE_STEPS.map(([forward, right]) => stepOffset(facing, forward, right))
);

/**
 * The furthest any row reaches, or 0. Derived — `MAX_ATTACK_RANGE`'s rule, and
 * for its reason: a row outrunning the sweep is check detection going blind.
 *
 * `attacks.ts` applies it whenever the board holds *any* variant, which is the
 * conservative trade — see `docs/claude/armies.md`.
 */
export const VARIANT_MAX_RANGE = Math.max(0, ...PIECE_VARIANTS.map((row) => row.range ?? 0));

/**
 * How far this row slides, or 0 to use its base rank's `SLIDER_RANGE`.
 *
 * **It overrides rather than raises**, and it used to raise. The turbo pair is
 * unmoved either way — 30 is longer than any rank — but a Confessor reaches
 * *five*, so `max` would have handed her a bishop's eight and quietly dropped
 * the whole trade. Both readers apply it the same way (`moveGen`'s ray bound
 * and `attacks.ts`'s `attackRange`) or check detection sees a line she cannot
 * take along.
 */
export const variantRange = (id: number): number => pieceVariant(id).range ?? 0;

/**
 * The leaps this row may take, as board offsets. Empty for all but the Pegasus.
 *
 * Defensive about the id for `pieceVariant`'s reason — a row is loaded from a
 * save — and the common answer is a shared empty array, never a fresh one.
 */
export const variantLeaps = (id: number): readonly number[] => VARIANT_LEAP_OFFSETS[id] ?? [];

/** The extra quiet steps this row may take, for a facing. Empty for most. */
export const variantExtraSteps = (id: number, facing: number): readonly number[] =>
  VARIANT_EXTRA_OFFSETS[id]?.[facing] ?? [];

/** Does this row move by its own table rather than as its base rank? */
export const variantMoves = (id: number): VariantMoves | undefined => pieceVariant(id).moves;

/** Does this row mend the pieces around it? `industry/healers.ts` is the rest. */
export const variantHeals = (id: number): boolean => pieceVariant(id).heals === true;

/** The row an id names. Out of range is plain. */
export const pieceVariant = (id: number): PieceVariant =>
  PIECE_VARIANTS[id] ?? PIECE_VARIANTS[VARIANT_PLAIN];

/**
 * Which variants a rank may take, in table order. Empty for most ranks.
 * **A fuse is not one** (§31 P4) — `fuse` makes it, `upgrade` never sells it.
 */
export const variantsFor = (type: PieceType): readonly number[] =>
  PIECE_VARIANTS.map((_row, id) => id).filter(
    (id) =>
      id !== VARIANT_PLAIN && PIECE_VARIANTS[id].base === type && PIECE_VARIANTS[id].fusedFrom === undefined
  );

/** Display names, here so the bar and the piece badges cannot drift apart. */
export const PIECE_NAMES: Record<PieceType, string> = {
  [PAWN]: "Pawn",
  [KNIGHT]: "Knight",
  [BISHOP]: "Bishop",
  [ROOK]: "Rook",
  [QUEEN]: "Queen",
  [KING]: "King"
};

/**
 * What a foundry may be told to build — every rank, the king included: a king
 * is an ordinary bill since §31 M4.
 */
export const MINTABLE: readonly PieceType[] = [PAWN, KNIGHT, BISHOP, ROOK, QUEEN, KING];

/** Hoisted out of `RULES`, which once derived `kingBuffer` from them. */
const MINER_RATE = 2;
const KING_TEMPO_MAX = 6;

export const RULES = {
  /**
   * No opening scrap on the *classic* board: it starts clean and every unit of
   * material there comes from a capture. Any board with room to spare gets
   * **deposits** instead (§26 F3, `ai/deposits.ts`) — a corpse is still the
   * metal line's wartime source, and stone and the specials have no other one.
   */
  seedSquares: [] as readonly string[],
  seedAmount: 0,

  /**
   * How far a field is generated from the army it is generated for
   * (Chebyshev). Ore under somebody's opening pieces is not ore anybody has to
   * reach for — and on a camp's ground the same rule puts the field on the
   * *approach*, which is what makes a faction fight over one without a line of
   * planner code behind it: working it means standing pawns on contested
   * ground, where the aggression term already sends the enemy.
   */
  depositClearance: 3,
  /** One patch per this many squares of clear ground, plus one. */
  depositEvery: 60,
  /** At most this many patches on one opening board. */
  depositPatches: 3,
  /**
   * How many tiles a patch runs to — a run's one field and a scenario board's
   * alike.
   *
   * **Tight, so a vein is rare land to fight over rather than painted floor.**
   * One pair for both branches because it is one decision: three to five
   * squares, each deep enough (`depositAmount`) that the field still holds what
   * a dozen-odd shallow tiles held, in a third of the board.
   *
   * **The target leads and the ground follows** — a field that cannot fit
   * brings a lobe of new squares with it, so this is a size somebody chose
   * rather than whatever the leftovers allowed. `Min` is also the floor below
   * which there is no field at all: a tile or two is a pile, which is what a
   * heap is and what a deposit is the opposite of.
   */
  depositTilesMin: 3,
  depositTilesMax: 5,
  /**
   * What a **drill** gets out of a tile in a turn, whatever `minerRate` says.
   *
   * One, matched to `handoffRate`: a pawn parked on a tile then extracts and
   * hands on at the same pace, so it is a steady feed into the lane beside it
   * with no buffer to jam. That is the whole feel — a deposit is **occupied**,
   * not grabbed, and at a miner's two a tile was gone in three turns.
   * A corpse's heap keeps `minerRate`: a body is picked over, not drilled.
   */
  depositDigRate: 1,
  /**
   * How often a board-opening event in a run also brings a field.
   *
   * **Rarity is bought here, not by starving a tile.** A vein holds as much as
   * ever; there are simply fewer of them, so finding one is an event and
   * holding one is worth a fight.
   */
  depositSpawnChance: 0.35,
  /**
   * Below this many un-mined units of the **scarcer** workhorse line, the next
   * opening brings a field whatever the roll said.
   *
   * A pity rule, because the chance is a *texture* and running dry is a dead
   * run: the economy is the way a run progresses, so a stretch of bad luck
   * must not be able to stop it. **Per line**, since metal is always carved
   * first: on the total it would let a run sit on a heap of iron and no stone
   * for ever, which is the playtest's own complaint. A board with no tiles at
   * all is nought on both lines and so is guaranteed by the same reading —
   * that is what opens the tutorial's first field.
   */
  depositLowWater: 20,
  /**
   * Units in an ordinary tile, and in the richer one the patch grew from.
   *
   * **A falloff rather than a flat charge**, because a patch richest in the
   * middle reads as a deposit at a glance where a slab of identical tiles
   * reads as painted floor. Two tiers, not three: a three-to-five tile blob
   * never leaves the seed's own ring on open ground, so a middle ring would be
   * a dial nothing ever read.
   *
   * Sized against `depositDigRate`, so a tile **occupies a pawn sixteen to
   * twenty-four turns** — the occupation this mechanic is about. Drafts, like
   * every number here.
   */
  depositAmount: 16,
  depositAmountSeed: 24,
  /** How many tiles a special micro-patch runs to. */
  depositSpecialMin: 1,
  depositSpecialMax: 2,
  /**
   * Units in a special tile — flat, with no seed tier, because a one-or-two
   * tile patch has no middle to be richest at.
   *
   * **Its own dial**, so deepening the workhorse veins does not multiply the
   * electrum and diamond supply as a side effect: a special is scarce by
   * `specialDigTurns`, and how much of it a board holds is a separate
   * decision that has not changed.
   */
  depositSpecialAmount: 10,
  /** How often a camp's ground also carries a special micro-patch. */
  depositSpecialChance: 0.25,
  /**
   * Odds of each tier for a generated mixed tile (§31 D1), by id: thin,
   * concentrated, rich (§33 M1's words), summing to one. A quarry has no tier. D2's zones replace
   * this with their own spawn tables.
   */
  depositTierOdds: [0.45, 0.4, 0.15] as readonly number[],
  /**
   * Turns of digging one unit of a **special** costs.
   *
   * A special is hunted, not strip-mined: a dig attempt spends the turn and
   * lifts nothing until the count is met, and then exactly one unit comes up
   * whatever the piece's harvest rate is. That rate is what makes electrum
   * scarce by *time* rather than by a number in a table.
   */
  specialDigTurns: 3,

  /**
   * What one belt part costs (§31 S2, design "Conveyor Belt Part"): three
   * cobble — the stone line at grade 0, any rung at or above, cheapest first —
   * paid from the inboxes of your pieces beside it, then your stores nearest
   * it (`beltFunds`), the moment it is laid. No blueprint, no iron.
   * `remove-belt` puts the three back on the ground; a wreck leaves them as
   * cobble on the floor beside the cargo's scrap.
   */
  beltCost: 3,
  /**
   * Fuel points every upgrade costs beyond its bill (§31 P3, design "How to
   * upgrade a piece"), drawn from the same source: its pool, the power
   * network reaching it, then coal it holds (`industry/upgrades.ts`).
   */
  upgradeFuel: 2,
  beltGrade: 0,

  /**
   * The most moves a side may be given in one turn.
   *
   * A ceiling rather than a setting: `movesPerTurn` arrives in the opening
   * command and is therefore something a client can send, so it needs a bound.
   * Eight is well past anything playable and far short of a number that makes
   * one turn take a visible age to resolve.
   */
  maxMovesPerTurn: 8,
  /**
   * Army Logistics (§33 T1): Fodder Moves a turn per Power Move. The doc's
   * base stat, 2X; Legion Mandate and MARCH raise it (T5).
   */
  armyLogistics: 2,

  /**
   * How often taking a piece worth taking leaves a work order behind.
   *
   * A quarter, so a card is a thing that happens rather than a thing you
   * count on — high enough that a run of exchanges produces one, low enough
   * that the deck is not a function of attrition. Pawns never drop one; a
   * pawn is what you trade.
   */
  workOrderChance: 0.25,

  /**
   * Moves the Reactor Boosters *add* to the turn it is played on.
   *
   * **An increment, where Triple Time was a target** (§26 C1). The old card
   * raised the turn to three moves however many you already had, so on a
   * Titan's two-move turn it was worth one move and a second copy was worth
   * nothing. Two more, summed, is worth the same on every turn and worth
   * playing twice. `maxMovesPerTurn` is still the bound.
   */
  reactorBoosterMoves: 2,

  /**
   * Moves a Confessor hands the piece beside her, and how often she may.
   *
   * **The grant is bounded by the piece, not by the turn**, which is what
   * makes it different from the Boosters above: those add two moves a player
   * spends anywhere, and these two belong to the piece she touched. So the
   * ceiling on it is a *position* — a bishop parked beside your queen — rather
   * than a card you happened to draw.
   *
   * Two rounds, so the turn she spends is roughly the tempo she gives back:
   * one move in and two moves out, every other round. Priced as a cooldown
   * rather than as material because she pays with the thing the ability makes
   * — the board's tempo — and a metal price would make her a second foundry.
   */
  empowerMoves: 2,
  empowerRounds: 2,

  /**
   * Health a Miracle Healer puts back into each hurt piece it touches, a tick.
   *
   * Paid for with the tick she would have cooked in and nothing else — a metal
   * price would charge twice for one machine's turn. Slow on purpose: the dial
   * is how long a wounded piece must stand beside her, which is the counterplay.
   */
  healRate: 1,
  /**
   * Damage a Unicorn Knight's `lance` does, for its whole turn.
   *
   * Three, so it is worth a turn against a Power and kills fodder. Through
   * `damage`, so a plate eats it first.
   */
  lanceDamage: 3,
  /**
   * Damage a Unicorn's picket does a round, for no turn at all.
   *
   * One: the horn is what you aim, this is what standing near it costs. A rate
   * and not a blow, because the counterplay is leaving.
   */
  picketDamage: 1,
  /**
   * How many of its own fodder a Paladin turns for its move (`rally`).
   *
   * Three, against a knight's `re-aim` of one: the turn buys the same act at
   * three times the width, which is the whole of what the row sells.
   */
  rallyPieces: 3,
  /**
   * Moves one Titan Relic adds to every turn while it stands.
   *
   * **It stacks**, and since §26 C1 so does the card — the two are the same
   * arithmetic now and the difference is what they are: this is a standing
   * ability whose whole subject is commanding a large army, and the Boosters
   * are spent in a moment. `maxMovesPerTurn` is the bound for both.
   *
   * One, and not two, because the tempo is not the only thing it buys: every
   * move ticks every side's factory, so each of these is also a permanent extra
   * industrial pass for everybody on the board.
   */
  titanMoves: 1,
  /**
   * A side's own factory ticks per turn, before buffs (§31 T3, the doc's base
   * stat). One follows each move; End Turn runs `max(1, this − ticks run)`.
   */
  ticksPerTurn: 2,
  /**
   * Extra factory ticks per turn a standing Titan Relic grants its controller,
   * per relic, on top of `ticksPerTurn` (§26 A2, §31 T3). Its own knob
   * rather than a second reading of `titanMoves`: the two grants are
   * different dimensions of the same structure and a sweep should be able to
   * move them apart.
   */
  relicTicks: 1,
  /**
   * A Titan Relic's price climbs by this many marble for each one the side
   * has placed before, held or lost (§31 A5): 10, 30, 50 … — linear.
   */
  relicStep: 20,
  /**
   * Rounds between Oil-Stone Scepter seizures (§31 A4): "one full round every
   * five rounds, including on the round they are placed".
   */
  seizeEvery: 5,
  /** Rounds (`Position.round`) a Stasis Field holds a piece. Rounds, not your moves — it locks anybody's (§26 C4). */
  stasisRounds: 3,
  /** Squares one Static Moat may wall at once, the designer's "up to three" (§26 C13). */
  moatSquares: 3,
  /**
   * Turns a king keeps a queen's movement under Programmer Oil (was Long Socks).
   *
   * Counted in rounds, as `stasisRounds` is (§31 T): one of your turns each,
   * whatever a turn holds.
   */
  programmerOilTurns: 3,
  /** Board tiles one Bridges banks, and the longest chain one bridge may be (§26 C9). */
  bridgeTiles: 5,
  /** Moves Yearning gives up — flat, floored at nought (§31 C1). */
  yearningMoves: 2,
  /** Ticks Yearning buys per move given up. A move is already worth one. */
  yearningTicks: 3,
  /** One-shot Off-Turn Production points The Enemy is at the Gates grants (§31 C4). */
  gatesPoints: 3,
  /** Ticks the Full Production Gambit buys — a count, not exhaustion (§26 C3). */
  fullProductionTicks: 12,
  /** How many cards a run holds in hand at the start of each of its turns. */
  handSize: 5,

  /**
   * Miner rate — **two**, which is more than one lane carries.
   *
   * It was one, on the argument that a miner should exactly saturate a lane so
   * that a second digger needs a second lane. Measured against play, that made
   * the industrial half unreachable: games run about 28 turns, and a knight is
   * six units that each have to be dug, hauled and cooked, so the first piece
   * a factory ever minted arrived at roughly the turn the game ended. Across
   * every tuning profile the correlation between game *length* and pieces
   * minted was **+0.92** — the economy was not being run well or badly, it was
   * simply being outrun by the war.
   *
   * Doubling extraction is the lever that does not touch the deflation
   * argument: a bill costing more than a corpse yields is the entire margin
   * keeping total material falling (see `FORGE_BILLS`), so making pieces cheaper is the change
   * that cannot be made freely. Making the floor come up faster can.
   *
   * The lane is now the bottleneck rather than the pit, which is the more
   * interesting constraint anyway: it is the one you can build your way out of.
   */
  minerRate: MINER_RATE,
  /** How much of a step of difficulty one cleared front is worth. */
  stagePace: STAGE_PACE,
  /**
   * How far a king's tempo can build.
   *
   * A king does one job a turn, and every turn it stays on that job its
   * capacity for it goes up by one — leave it refining and it works through a
   * backlog faster and faster. Capped because the ramp is otherwise unbounded
   * and the Productivity Gambit runs the economy to exhaustion: an
   * ever-growing capacity there turns a settling loop into one that clears any
   * backlog in a single round, which is not a phase change, it is a switch.
   */
  kingTempoMax: KING_TEMPO_MAX,
  /**
   * Kings and queens move this many units a turn when passing material on.
   *
   * They are the two pieces with the range to be somewhere useful and the
   * standing to be worth routing through, and at one unit a turn a bucket
   * brigade through either was slower than the belt it was standing beside.
   */
  royalHandoffRate: 2,
  pawnBuffer: 20,

  /**
   * How keen each kind of machine is for a unit it can use, as a head start in
   * squares on the routing field (`industry/routing.ts`). Lower is keener, and
   * the gap between two tiers is how far out of its way material will go for
   * the better one.
   *
   * This replaced a strict ranking — every refinery on the board beat every
   * foundry, at any distance — which is what let a rook on the far side of the
   * board pull ore past the oven standing next to it. A tier is now a
   * *preference measured in squares*, so the argument survives ("scrap is worth
   * more cooked, and a foundry that eats it first takes it out of the grade
   * ladder for good") without being worth an unbounded walk.
   *
   * A store is the exception and stays a strict last resort: `store` is far
   * larger than any board is wide, so a warehouse only ever wins when nothing
   * else is reachable at all. A store that outranked an oven would swallow
   * every unit of ore and halt the factory with a full warehouse — PLAN §14's
   * most important tuning decision, and it is preserved exactly.
   */
  demand: {
    /** An oven that could raise this unit's grade. */
    refiner: 0,
    /**
     * A part that has been drawn and not yet paid for.
     *
     * **Above a refiner and below a hungry foundry**, and the bound that
     * matters is the first one: an oven standing next to the miner should
     * still win the ore, or scrap stops being worth more cooked the moment
     * anybody sketches a lane. What it buys by sitting under `hungryFoundry`
     * is that a blueprint one square away beats an oven three squares away —
     * which is the case the tier exists for, since an unfunded run is the one
     * thing on the board that makes the rest of the network *not work*.
     *
     * Cheap to be wrong about, unlike every other tier here: a blueprint wants
     * exactly one unit and then stops being a destination at all.
     */
    blueprint: 1,
    /** A foundry saving for a recipe this unit's grade can pay into. */
    hungryFoundry: 2,
    /** A foundry with nothing set: somewhere to put it, not somewhere it is wanted. */
    foundry: 6,
    /** Nothing else will have it. */
    store: 1_000_000,
    /**
     * **A stage is charged for its own output's journey.**
     *
     * How an assembly line comes out of a routing rule instead of being drawn
     * by hand: a machine's demand is its tier plus this fraction of how far
     * what it *makes* would then have to travel. An oven with a foundry behind
     * it is a keener destination than an identical oven in a corner, so ore
     * flows to the one whose output has somewhere to go — which is the same
     * thing as saying the stages want to be in order between the source and
     * whatever finally spends the metal.
     *
     * A fraction rather than the whole, because the second leg is a *later*
     * problem and the material is not there yet.
     *
     * One level deep and no further: the lookahead field is built flat, so the
     * cost is one extra search per distinct thing a machine would produce, and
     * there is no recursion to bound.
     */
    onward: 0.5,
    /**
     * The most an oven can be charged for where its output goes.
     *
     * **Strictly below `foundry`, and that bound is the rule.** The whole term
     * is a comparison *between ovens*; let it grow past the gap between the
     * tiers and it stops being that and starts deciding whether ore is cooked
     * at all — a forge would out-bid an oven simply for standing nearer its own
     * downstream, and "scrap is worth more cooked, and a foundry that eats it
     * first takes it out of the grade ladder for good" would quietly stop being
     * true. Measured when the tiers were 0/1/3: at a cap of 2 a rook told to
     * pass its scrap to the bishop above it kept it instead, because the round
     * trip back to the rook cost the bishop exactly the gap.
     */
    onwardCap: 4,
    /**
     * What an oven with nowhere at all to send its output is charged instead.
     *
     * **Above `foundry` on purpose**, which is the one place this term is meant
     * to outrank the tiers: a forge that can spend the metal now beats an oven
     * that would raise its grade and then sit on it. Not `Infinity` — an oven
     * with nowhere to put its output is still the right place for ore when it
     * is the only machine on the board, and a run opens exactly that way.
     */
    stranded: 12
  },

  /** Refinery: quality not quantity. One unit per turn, one turn in the oven. */
  refineryRate: 1,
  /**
   * Twenty (2026-11-03, was four, was two): every piece's bank is 20 by the
   * designer's ruling (SPEC-ADDENDA A2). A metal rung still needs a coal
   * beside it, so half of it is iron at most while no fuel is in hand
   * (`machineAccepts`, A1).
   */
  refineryInputBuffer: 20,
  /**
   * Coal a refiner keeps at the least (§31 M2) — `coalWanted` raises it to one
   * per metal rung waiting.
   */
  refinerCoal: 1,
  /**
   * The king's input side, bigger than a bishop's because the king harvests
   * into it: at a 2-unit cap its three-a-turn dig could only ever bring up
   * two, and the rate would be a number that never happened.
   *
   * Sized to a dig at full tempo — rate x factor x `kingTempoMax` — for the
   * same reason. At 6 it held two turns' worth, so a king that had built up
   * to four hit the ceiling instead of the number it had spent four turns
   * earning, and the tempo silently stopped meaning anything past turn two.
   */
  /**
   * The king's hopper.
   *
   * It refines out of this and forges out of it, and it accepts anything a
   * belt brings or a neighbour hands over — so it is the widest inbox on the
   * board and deliberately so. A hopper that backs up stops a lane, and a lane
   * that stops is the thing a player notices least.
   *
   * Twenty, every piece's inventory (§33 I1; was twice the dearest bill,
   * 36). The queen's 18 still fits, so a king can bank one; a bigger
   * stockpile starts in a store (Training's, §33 I1).
   */
  kingBuffer: 20,
  refineryOutputBuffer: 20,

  /**
   * A rook's hopper: a foundry takes whatever arrives, and what it can build
   * from that is decided when the player forges. Twenty, every piece's
   * inventory (§33 I1, was 40) — the dearest bill, the queen's 18, still fits.
   */
  foundryBuffer: 20,
  /**
   * The queen hauls what she sweeps until she can reach a belt, so unlike the
   * other non-machines she needs somewhere to put it. Generous on purpose: a
   * long sweep across a fought-over board should not spill.
   */
  queenBuffer: 20,
  /**
   * The knight is the one piece with no machine role, so without a buffer it
   * could not hold material at all — and a line of touching pieces could not
   * pass anything through it. Twenty, like every piece's bank (A2).
   */
  carrierBuffer: 20,
  /**
   * How often the spawn clock lands a camp, in rounds (`Position.round`,
   * §31 T) — the unit `clear-front`'s delay counts in too (§26 F10).
   *
   * Only read under `ruleset.spawn === "clock"`, where camps arrive on this
   * clock whether or not the last one is beaten. Forty keeps the old pace:
   * eighty plies of a 1v1 were forty of the player's own turns. Rounds do not
   * tighten as camps stack up the way plies did. F13's Hard mode scales it by
   * 0.8 at its own read site (32).
   */
  spawnEvery: 40 as number,
  /**
   * The first stage a front may open as *two* camps at once.
   *
   * Late, because a pair is not a harder camp — it is a second front, and the
   * whole shape of a turn changes when there is one behind you as well as in
   * front. By stage eight a run has an army, a factory and somewhere to fall
   * back to, which are the three things that make two fronts a decision rather
   * than an execution.
   */
  pairFromStage: 8 as number,
  /** How often a stage past that opens as a pair, out of one. */
  pairChance: 0.35,
  /**
   * How far a regrowth seed carries.
   *
   * The counter it starts with, handed on one lower each step — so this is
   * both the reach in squares and the number of turns the round takes to lay
   * it. Sized so a regrowth is worth taking when the board has been eaten
   * down to nothing: it has to give back somewhere to stand, not a corridor.
   */
  growDepth: 6 as number,
  /**
   * The chance a newly grown square arrives as a promotion square.
   *
   * Small, and it is what makes regrowth a *choice* rather than a consolation:
   * a board that has been eaten is a board with nothing left to fight over,
   * and a promotion square is the one thing worth walking to. Rolled per
   * square off the seeded stream, so a replay lays the same ground.
   */
  growPromotion: 0.08,
  // `turretTiers` retired by §31 S8: a turret's numbers are its gun's row
  // (`TURRET_GUNS`); Targeting Arrays past the first scale it. §26 M2's
  // cap went with A7.

  /**
   * What a unit of each (line, rung) is worth, cross-line — **one price
   * table**, because every "carry the dearest, spend the cheapest" rule needs
   * one answer to "marble vs steel". The reducer routes by it (`takeUnit`
   * spends up it, `unitPreference` carries down it), so it is a rule and not a
   * weight; the tuner's per-line copies are `w.grade` / `w.gradeStone` /
   * `w.gradeSpecial`. Indexed `[line][grade]` in `METAL`/`STONE`/`SPECIAL`
   * order. First numbers are a draft, to be fixed by play and the tuner.
   */
  materialValue: [
    // Metal, three rungs (§31 M1). The old grades 3–4 were only a yardstick;
    // that is `VALUE_RUNGS` now.
    [1, 3, 6],
    [1, 2, 4],
    // Electrum, diamond.
    [12, 24],
    // Coal, nuclear. Coal at iron's price: both are raw and both are burned or
    // cooked a unit at a time. Nuclear is a draft (S6 produces it).
    [1, 12]
  ] as readonly (readonly number[])[],

  /**
   * What the rocket costs, and what it is made of (PLAN §12.4.9).
   *
   * **The first thing that needs the whole grade ladder at volume.** Cooking is
   * 1:1 and a unit may never be refined twice by the same machine, so every
   * unit of Diamond is one unit of ore that has passed through *three
   * different* refineries. A rocket is therefore not a big number, it is a
   * **line**: dig, three ovens, a lane to the pad. Nothing before it has
   * required the factory to be finished.
   *
   * The landmark is **eight queens** — a sacrificed queen is 9 Diamond, and
   * eight is the number every chess player will assume — so 72 is what the pure
   * sacrifice route costs. This is deliberately under it: sacrifice is *a* path
   * and not *the* path, and the level has to be winnable by industry alone, by
   * sacrifice alone, and most naturally by both.
   *
   * **Untuned.** §12.4.9 says to tune it against a line that is *running*,
   * never against a stockpile, and that measurement wants the last level to
   * exist first. §12.9's S10 and S11 are the rows that grade it.
   */
  // **The rocket burns diamond** (§26 F2, decided): the diamond *special* —
  // hunted and hauled out of deposits, never cooked. `grade` is a **rung of
  // `VALUE_RUNGS`**: a deliver objective counts any unit worth at least
  // `valueRung(grade)`, so the level format never learns lines. 4 is diamond.
  rocketCost: { units: 36, grade: MAX_GRADE } as { units: number; grade: number },

  /**
   * No ration and no cap: construction is limited by territory instead (PLAN
   * §2, amended). `beltCapacity` still bounds what one part carries.
   */
  /**
   * Units one belt part holds (§33 I3, the doc's "smaller Inventory than
   * normal structures of 5"). Was 1 and read nowhere: a pile ran to
   * `MAX_UNITS`. `pushUnit` caps a cargo pile here; a pile moves on whole or
   * not at all (`advanceBelts`).
   */
  beltCapacity: 5,

  /**
   * Safety backstop on the Productivity Gambit's fixpoint. The loop already
   * terminates on *progress* rather than on change, so a ring of belt parts
   * circulating cargo forever cannot hold it open — this is belt and braces.
   */
  gambitRoundCap: 64,

  /**
   * How many turns an army is given to look for a way out before it lays down
   * its arms (`Position.despair`).
   *
   * **One: the grace period** (§26 F9). Depression is measured by a
   * *simulation* — could this army win if its opponent stopped moving — and a
   * simulation is a judgement, so an army that is one lucky capture from a
   * mating piece gets a turn to make it, or to put another piece on the board
   * (`destitute`'s gate is what "must construct another piece" means). The
   * stamp clears the moment the answer changes, so the turn is a reprieve
   * rather than a sentence with a delay. Not zero here: Hard's "no grace
   * period" is F13's run-mode dial, which scales this at the read site rather
   * than forking the constant.
   */
  despairTurns: 1,

  /** Rounds an IFF Spoofer lasts (§26 C12), on `Position.round`. */
  disguiseRounds: 5,

  /**
   * How far ahead the depression check looks, in turns of the army's own.
   *
   * Sixty-four is long enough to mine a heap, cook it and forge a rook — the
   * cheapest path from "cannot mate" to "can" — so an army with a factory and
   * a graveyard is *not* depressed, which is the whole reason this is a
   * simulation rather than the insufficient-material test it looks like.
   */
  despairHorizon: 64,

  /**
   * Material an army must be *below* before despair is even considered.
   *
   * **The simulation is not allowed to be the only gate, because it is a
   * judgement about how well the position can be played and it was losing
   * that argument.** Measured: a lone king with forty scrap one square away
   * ran the full sixty-four turns and never mined once — the cheap planner
   * simply never stepped onto the heap — so a factory sitting on its raw
   * material was condemned as hopeless. It killed a run three turns in, which
   * is the mechanic doing the exact opposite of its job. A rollout that fails
   * to find a win is evidence about the *planner*; what an army holds is a
   * fact about the position, and a fact should gate a judgement rather than
   * the other way round.
   *
   * Two bishops at `mintCost` apiece, derived rather than picked: that is
   * the cheapest pile of raw material that becomes a forced mate, since a
   * bishop is the cheapest recipe at a grade one oven can reach and K+B+B
   * mates where K+N+N does not. Below it there is genuinely nothing to build
   * with; at or above it the factory has an argument, and the whole point of
   * `ai/depression.ts` being a simulation rather than the insufficient-
   * material rule is that this game lets you build your way out.
   */
  despairMaterial: mintCost(BISHOP) * 2,

  /**
   * Non-pawn pieces at which an army is beyond consideration entirely.
   *
   * Two, counting the king — so the gate only ever closes on a lone king with
   * a pawn or two behind it. A second real piece is a second oven, a second
   * foundry or a mating piece outright, and an army with one of those is
   * behind rather than in a coma.
   *
   * Pawns are excluded because an army can mint them for two iron each: they
   * are what a desperate factory produces, so counting them would let an army
   * spend its way out of a diagnosis rather than out of the position.
   */
  despairPieces: 2,

  /**
   * The furthest a queen, a rook or a bishop may travel in one move.
   *
   * Ten (§31 K3) — past the longest slide the classic board allows, so it
   * changes nothing until the board grows past it. See `SLIDER_RANGE`.
   */
  sliderRange: 10,

  /**
   * **A structure's inventory unless its row says otherwise** (§33 I2, the
   * design's "unless otherwise stated, a structure has an inventory size of
   * 50"). Read through `inventoryOf`; was `storeCapacity` 80.
   */
  structureInventory: 50,
  /** Loose material a square holds before it spills (§33 I4): heap and ground. */
  looseCap: 20,
  /**
   * What a store costs, and the grade it takes.
   *
   * Priced at a minor piece, from silver. A building is a real investment —
   * it spends the forge and it takes a square off the board for good — and
   * anchoring it to a knight makes the trade legible: this warehouse or that
   * horse.
   */
  storeCost: 6,
  storeGrade: 1 as Grade,
  /**
   * What it takes to knock a building down (§27 applied to §26 F5).
   *
   * **Ten, which is one more than the biggest blow in the game.** No single
   * piece can take a warehouse in one move — a queen at nine leaves it
   * standing on one — so a building is ground down over turns rather than
   * lost to whatever wandered past it. That is the whole point of the number:
   * a structure spends the forge and takes a square off the board for good,
   * and losing one to a single knight hop made the investment unreadable.
   *
   * One number for every kind, through `STRUCTURE_HEALTH` — the same shape
   * `STRUCTURE_COST` takes, so a kind that wants its own row has a row to
   * write and no second mechanism to build.
   */
  structureHealth: 10,
  /**
   * Structure HP a non-jumping move may plow through (§31 S1, design
   * "Infrastructure is Vulnerable"). A capture at the end deals
   * `captureDamage` less what it spent; past the budget the move stops.
   */
  plowBudget: 5,
  /**
   * Units a store hands out per tick.
   *
   * Two, matching a king's and a queen's: it is a loading bay rather than a
   * hand, and a warehouse that emptied one unit at a time would be slower than
   * the lane standing next to it — which is the complaint that raised the
   * royal rate in the first place.
   */
  storeHandoffRate: 2,
  /**
   * What dismantling gives back, as a share of the build cost, at the grade it
   * was paid in. Half: taking a thing apart on purpose is not the same as
   * having it wrecked, but a building you can put up and take down for free is
   * a building you would never think about where to put.
   */
  dismantleRecovery: 0.5,
  /**
   * Cards a trophy offers (§31 A2), each rolled off `MONARCH_PACK` when the
   * trophy is earned; the player takes one or keeps the trophy.
   */
  packSize: 3,
  /** What a trophy kept rather than exchanged adds to the run's score (§31 A2). */
  trophyScore: 10,
  /**
   * What each structure kind is worth to the opponent's capture pricing,
   * as a multiplier over the searchable `w.structureValue` (§26.6 11).
   * Indexed by kind: STORE, TURRET, ALTAR, RELIC, (4, the retired Banner),
   * KEYSTONE, WELL, (7, the retired Research Station), then §31 S4–S6:
   * Simple, Firm, Reinforced Wall, Armory, Coal and Nuclear Generator,
   * Battery, Cabling Tower; §31 S7: Track, Train Engine, Train Car; §31
   * A6–A9: Fusion Chamber, Shield Generator, Pillar of Excellence; §31 S8:
   * Kinetic and Cannon Turret; §31 A10–A13: Propaganda Tower, House of Cards,
   * Endurance Monument, Fountain; §33 T5: Legion Tactics Tower; §33 I5:
   * Drone-Assisted Warehouse — guesses, the planner builds none of them.
   *
   * **A rules table, not seven weights**: what a Relic is worth against a
   * store is a fact about the rules — the same split `w.ownPiece` makes over
   * `w.piece` — and without it a camp guards a warehouse while its enemy's
   * Relic grants two moves a turn. The Relic is the outlier on purpose: it is
   * the loudest tempo in the game, and it is acceptable *because* it has an
   * address somebody can walk to.
   */
  structureWorth: [1, 1.5, 2, 4, 2, 2, 2, 2, 0.5, 1, 1.5, 2, 3, 4, 3, 1, 0.5, 2, 1.5, 2, 3, 4, 1.5, 2, 3, 3, 3, 4, 3, 2, 2],
  /**
   * What Hard Mode changes, in one block (§26 F13).
   *
   * **The run's constitution, not the opponent's competence.** The
   * easy/normal/hard picker sets two things: the skill ceiling a run climbs to
   * (`levelForStage`, per camp, and it *climbs*) and this — three numbers in
   * force from turn one. They are read through `campaign/mode.ts` and nowhere
   * else, so a normal run reads the constants beside this block exactly as it
   * always did.
   */
  hardMode: {
    /**
     * **No grace period**, which is the designer's own words. `despairTurns`
     * is one everywhere else — a turn to put another piece on the board — and
     * nought here means the surrender goes out on the turn the diagnosis
     * lands. Scaled at the read site rather than forking the constant, so the
     * banner, the reducer's gate and `despairStep` stay one subtraction.
     */
    despairTurns: 0,
    /**
     * How much shorter the spawn clock runs: "20% shorter" read as a scale on
     * `spawnEvery`, rounded, so one number moves and the interval stays a
     * whole count of rounds. Only ever read under
     * `ruleset.spawn === "clock"`, which is what the text says too.
     */
    spawnScale: 0.8,
    /**
     * One tier of camp strength, added to the paced stage in `buildWave`.
     *
     * It bumps the formation drawn *and* the ranks `reinforce` hands back,
     * which is how the curve already couples them — and both halves are what
     * "spawn with more pieces" means, since `reinforce` alone only upgrades
     * pawns in a camp of the size the stage would have drawn anyway. The
     * `skill.reinforcementTiers` cap still applies: how hard a camp may get is
     * the *skill*'s to bound, and this rides underneath it.
     */
    strengthBonus: 1
  }
} as const;

/**
 * What each structure kind *is* on the board (§31 S1): its HP and whether it
 * is an **Obstruction**. One row per kind, keyed by the kind number
 * (`src/shared/industry.ts`), so a wall (S4) is a row and not a mechanism.
 *
 * - **Obstruction**: blocks non-jumping movement and ranged line of sight; a
 *   blow at it destroys it (mover takes the square) or bounces.
 * - **Otherwise**: an enemy plows through it (`plowBudget`), a piece may stand
 *   on it, and a shoved piece landing on it breaks it.
 *
 * `liftsAdjacency` is S3's hook for the design's unnamed "special high cost
 * structure": while one stands under your control, building needs no adjacent
 * piece. No row sets it yet.
 */
export interface StructureRow {
  readonly obstruction: boolean;
  readonly health: number;
  readonly liftsAdjacency?: boolean;
  /**
   * The build bill (§31 S4–S6, the design's table). A wreck drops its stone
   * and metal rows as grade 0 of their line; specials and fuel are consumed.
   * An artifact kind's bill is paid at `place-structure` (§31 A3–A5,
   * `campaign/artifacts.ts`).
   */
  readonly bill: readonly ForgeRow[];
  /** `build` puts it up (S3's rule). Unset: a token kind, or the turret's own door. */
  readonly buildable?: boolean;
  /** Takes no ranged damage — Firm and Reinforced Walls (S4). */
  readonly rangedImmune?: boolean;
  /** Built only over an own structure of this kind, which it replaces (S4). */
  readonly over?: number;
  /** May also be built over an own structure of this kind (Firm over Simple, S4). */
  readonly replaces?: number;
  /** Only on an upgrade site — an Armory of yours in reach (S4's Reinforced, S5). */
  readonly needsArmory?: boolean;
  /** Holds coal and nothing else (S6's Coal Generator, S7's Train Engine). */
  readonly burnsCoal?: boolean;
  /**
   * A train (§31 S7): built only over an own Track, which it stands on and
   * carries beneath it — the track comes back when the train moves off.
   */
  readonly onTrack?: boolean;
  /**
   * Material it holds (§33 I2); absent is `RULES.structureInventory`, 0 none.
   * Fuel points are not inventory (the battery's charge is its own).
   */
  readonly inventory?: number;
  /**
   * A stockpile (§33 I2): taps, routing fills it, builds and upgrades pay from
   * it (`storesNearest`). The store, the Armory and the Train Car.
   */
  readonly stockpile?: boolean;
}

/** A stone row: cobble 0, stone 1, marble 2. */
const stoneRow = (grade: number, count: number): ForgeRow => ({ line: STONE, grade, count });
const STORE_BILL = [stoneRow(RULES.storeGrade, RULES.storeCost)];

export const STRUCTURE_ROWS: Readonly<Record<number, StructureRow>> = {
  // STORE: a warehouse is masonry — obstruction, ten as before.
  0: { obstruction: true, health: RULES.structureHealth, bill: STORE_BILL, buildable: true, stockpile: true },
  // **Turrets (§31 S8).** Kind 1 is the Laser: 8 marble, 3 HP. The design
  // marks no turret an obstruction. Each may go over an own Reinforced Wall
  // (`replaces`): an emplacement — HP added, ranged-immune, an obstruction
  // (`turretEmplaced`). Built through the Targeting Array's door, not `buildable`.
  1: { obstruction: false, health: 3, bill: [stoneRow(2, 8)], replaces: 10 },
  // ALTAR, RELIC, KEYSTONE, WELL: the universal artifact rule — obstruction, 5.
  // Scrap Altar 10 stone (§31 A3); Titan Relic 10 marble, the first — the
  // climb is `RULES.relicStep` (A5); Oil-Stone Scepter 20 coal (A4).
  2: { obstruction: true, health: 5, bill: [stoneRow(1, 10)] },
  3: { obstruction: true, health: 5, bill: [stoneRow(2, 10)] },
  // KEYSTONE is the Targeting Array's Mainframe (§31 A7): 20 marble.
  5: { obstruction: true, health: 5, bill: [stoneRow(2, 20)] },
  6: { obstruction: true, health: 5, bill: [{ line: FUEL, grade: 0, count: 20 }] },
  // **Walls (§31 S4).** Simple: 5 cobble, 5 HP. Firm: 7 stone, the Simple's
  // 5 HP, ranged-immune, may replace a Simple. Reinforced: 10 marble, 10 HP,
  // ranged-immune, only over a Firm, only on an upgrade site.
  // Walls hold nothing (§33 I2).
  8: { obstruction: true, health: 5, bill: [stoneRow(0, 5)], buildable: true, inventory: 0 },
  9: {
    obstruction: true,
    health: 5,
    bill: [stoneRow(1, 7)],
    buildable: true,
    rangedImmune: true,
    replaces: 8,
    inventory: 0
  },
  10: {
    obstruction: true,
    health: 10,
    bill: [stoneRow(2, 10)],
    buildable: true,
    rangedImmune: true,
    over: 9,
    needsArmory: true,
    inventory: 0
  },
  // **Armory (§31 S5).** 15 stone + 5 marble, 3 HP. No Obstruction keyword
  // in the design, so it is plowed. A stockpile of 200 (§33 I2).
  11: {
    obstruction: false,
    health: 3,
    bill: [stoneRow(1, 15), stoneRow(2, 5)],
    buildable: true,
    inventory: 200,
    stockpile: true
  },
  // **The power network (§31 S6).** Generators and the battery are
  // obstructions at 3 HP; the cabling tower is plowed at 1.
  12: {
    obstruction: true,
    health: 3,
    bill: [stoneRow(1, 25), { line: METAL, grade: 0, count: 2 }],
    buildable: true,
    burnsCoal: true,
    // 200 coal (§33 I2), was 20.
    inventory: 200
  },
  // Nuclear, battery and cabling tower hold no material (§33 I2).
  13: {
    obstruction: true,
    health: 3,
    bill: [stoneRow(2, 50), { line: SPECIAL, grade: 1, count: 1 }],
    buildable: true,
    inventory: 0
  },
  14: {
    obstruction: true,
    health: 3,
    bill: [stoneRow(2, 20), { line: SPECIAL, grade: 0, count: 2 }],
    buildable: true,
    inventory: 0
  },
  15: {
    obstruction: false,
    health: 1,
    bill: [stoneRow(1, 5), { line: METAL, grade: 1, count: 1 }],
    buildable: true,
    inventory: 0
  },
  // **Trains (§31 S7).** Track: 4 stone + 1 iron, 1 HP, plowed. Engine: 6
  // marble, 10 HP, an obstruction, holds coal. Car: 6 marble, 8 HP, an
  // obstruction. Both trains go up over an own Track only. Track holds
  // nothing, the engine 20 coal, the car is a stockpile of 100 (§33 I2).
  16: {
    obstruction: false,
    health: 1,
    bill: [stoneRow(1, 4), { line: METAL, grade: 0, count: 1 }],
    buildable: true,
    inventory: 0
  },
  17: { obstruction: true, health: 10, bill: [stoneRow(2, 6)], buildable: true, burnsCoal: true, onTrack: true, inventory: 20 },
  18: { obstruction: true, health: 8, bill: [stoneRow(2, 6)], buildable: true, onTrack: true, inventory: 100, stockpile: true },
  // **Artifacts (§31 A6–A9)**, the universal rule: obstruction, 5 HP.
  // Fusion Chamber 15 stone + 5 marble (A8, the Armory's bill); Shield
  // Generator 30 marble (A9); Pillar of Excellence 10 diamond (A6).
  19: { obstruction: true, health: 5, bill: [stoneRow(1, 15), stoneRow(2, 5)] },
  20: { obstruction: true, health: 5, bill: [stoneRow(2, 30)] },
  21: { obstruction: true, health: 5, bill: [{ line: SPECIAL, grade: 1, count: 10 }] },
  // Kinetic Turret 6 marble, 4 HP; Cannon Turret 12 marble, 5 HP (§31 S8).
  22: { obstruction: false, health: 4, bill: [stoneRow(2, 6)], replaces: 10 },
  23: { obstruction: false, health: 5, bill: [stoneRow(2, 12)], replaces: 10 },
  // **Artifacts (§31 A10–A13).** Propaganda Tower 15 marble + 2 steel (Stoke
  // the Forges); House of Cards 30 marble (My Deck Runith Over); Endurance
  // Monument 30 marble + 10 crucible steel (Yield Not); Fountain of
  // Fiendishness 25 electrum + 25 diamond (Flow Faster).
  24: { obstruction: true, health: 5, bill: [stoneRow(2, 15), { line: METAL, grade: 1, count: 2 }] },
  25: { obstruction: true, health: 5, bill: [stoneRow(2, 30)] },
  26: { obstruction: true, health: 5, bill: [stoneRow(2, 30), { line: METAL, grade: 2, count: 10 }] },
  27: {
    obstruction: true,
    health: 5,
    bill: [
      { line: SPECIAL, grade: 0, count: 25 },
      { line: SPECIAL, grade: 1, count: 25 }
    ]
  },
  // **Legion Mandate (§33 T5)**: the Legion Tactics Tower, 22 marble.
  28: { obstruction: true, health: 5, bill: [stoneRow(2, 22)] },
  // **Drone-Assisted Warehouse (§33 I5)**: 50 stone, 25 HP, an obstruction,
  // only over an own store (whose stock it keeps), a stockpile of 500 that
  // supplies within `WAREHOUSE.reach` (`industry/warehouse.ts`).
  29: { obstruction: true, health: 25, bill: [stoneRow(1, 50)], buildable: true, over: 0, inventory: 500, stockpile: true },
  // **Empower Tower (§33 S1)**: 1 HP, 10 marble + 1 diamond, no inventory;
  // works only in pairs near a fuel source (`industry/empower.ts`).
  30: {
    obstruction: false,
    health: 1,
    bill: [stoneRow(2, 10), { line: SPECIAL, grade: 1, count: 1 }],
    buildable: true,
    inventory: 0
  }
};

/**
 * **The Empower Tower** (§33 S1). Towers within a fuel source's network
 * reach count toward it; `towersPerMove` of them grant one extra move a turn
 * to a piece within `reach` (Manhattan, §33.1 Q3) of the source, costing
 * `fodderFuel` or `powerFuel` network points, outside the pools.
 */
export const EMPOWER = {
  reach: 22,
  towersPerMove: 2,
  fodderFuel: 3,
  powerFuel: 10
} as const;

/** **The Drone-Assisted Warehouse** (§33 I5): its Chebyshev supply radius. */
export const WAREHOUSE = {
  reach: 5
} as const;

/**
 * **A turret's gun** (§31 S8, the design's table), keyed by kind. `shots` a
 * round; `reach` is a Chebyshev radius (`radius`) or a cone's depth
 * (`cone`: layer k is k squares out on the facing, 2k−1 wide). Each powered
 * Targeting Array past the first adds `arrayShots` shots and `arrayDepth`
 * layers. `order`: `random` (off `streamFor`), `near` or `far` layer first,
 * each layer swept left→right, the next right→left. Fuel: one point a shot
 * (`fuelPerShot`) or one a round. `ammo`: one unit a shot, at or above that
 * rung, cheapest first.
 */
export interface GunRow {
  readonly damage: number;
  readonly shots: number;
  readonly shape: "radius" | "cone";
  readonly reach: number;
  readonly arrayShots: number;
  readonly arrayDepth: number;
  readonly order: "random" | "near" | "far";
  readonly fuelPerShot: boolean;
  readonly ammo?: { readonly line: number; readonly grade: number };
}

export const TURRET_GUNS: Readonly<Record<number, GunRow>> = {
  // Laser: 1 damage, radius 3, 2 a round at random, +2 per extra array, 1 fuel a shot.
  1: { damage: 1, shots: 2, shape: "radius", reach: 3, arrayShots: 2, arrayDepth: 0, order: "random", fuelPerShot: true },
  // Kinetic: 2 damage, 5-deep cone, 2 a round nearest first, +1 per extra
  // array, 1 fuel a round, 1 marble a shot.
  22: {
    damage: 2,
    shots: 2,
    shape: "cone",
    reach: 5,
    arrayShots: 1,
    arrayDepth: 0,
    order: "near",
    fuelPerShot: false,
    ammo: { line: STONE, grade: 2 }
  },
  // Cannon: 5 damage, 3-deep cone, 1 a round farthest first, +1 layer per
  // extra array, 1 fuel a round, 1 steel or crucible steel a shot.
  23: {
    damage: 5,
    shots: 1,
    shape: "cone",
    reach: 3,
    arrayShots: 0,
    arrayDepth: 1,
    order: "far",
    fuelPerShot: false,
    ammo: { line: METAL, grade: 1 }
  }
};

/**
 * Turret numbers the design leaves open (§31 S8): ammunition a Kinetic or
 * Cannon holds; the `streamFor` salt a Laser's pick draws on.
 */
export const TURRETS = {
  laserStream: 151
} as const;

/**
 * The trains' numbers (§31 S7, the design's table). One fuel point moves an
 * engine up to `squaresPerPoint` squares; it burns `coalPerPoint` coal for
 * one, at most once a tick — so a train runs at most five squares a tick.
 */
export const TRAINS = {
  coalPerPoint: 3,
  squaresPerPoint: 5,
  /** Pieces a car carries. */
  carRiders: 2,
  /** Cars one engine pulls. */
  maxCars: 10,
  /** Boardings into one car per turn, and alightings out of it. */
  loadsPerTurn: 2
} as const;

/**
 * The power network's numbers (§31 S6, the design's table): fuel points a
 * generator makes per tick, its reach, the battery's store, the tower's
 * extension, and the nuclear blast.
 */
export const POWER = {
  /** Chebyshev radius every source, battery and tower reaches. */
  reach: 4,
  coalRate: 5,
  nuclearRate: 10,
  // 200 since V2 (§33 I6), was 50.
  batteryCapacity: 200,
  blastRadius: 4,
  blastDamage: 10,
  /** An Armory's upgrade site (§31 S5): pieces within this Chebyshev radius. */
  armoryReach: 2
} as const;

/**
 * **Deflector Shields** (§31 A9, the design's numbers). A Shield Generator
 * whose badge is borne takes `fuelPerRound` points from its owner's network
 * over the owner's turn (the "structures" tier, first) and is **lit** for
 * the round from that turn's End Turn. Reach is the design's "30 orthogonal,
 * 25 diagonal": Chebyshev ≤ 30 with the diagonal leg ≤ 25 — an octagon.
 */
export const DEFLECTOR = {
  fuelPerRound: 5,
  orthogonal: 30,
  diagonal: 25,
  /** Damage each lit ward takes off. */
  ward: 1,
  /** Generators that stack on one square. */
  maxStack: 3
} as const;

/**
 * **Artifacts A10–A13** (§31, the design's numbers).
 *
 * - `stokePoints`: Off-Turn Production points one powered Stoke the Forges
 *   grants (A10). One per side may stand.
 * - `brass`: capture damage the Unrelenting Knuckles of Brass add to the
 *   bearer's blow (A11); `houseTicks`: own factory ticks per card reward.
 * - `defiance`: the Shield of Defiance's armor (A12); `obstructionBulwark`
 *   and `structureBulwark`: max HP the side's buildings gain while on.
 */
export const ARTIFACTS_LATE = {
  stokePoints: 1,
  brass: 2,
  houseTicks: 20,
  defiance: 10,
  obstructionBulwark: 3,
  structureBulwark: 1
} as const;

/**
 * **Army Logistics sources** (§33 T5, the design's numbers). `legion`: what
 * one powered Legion Mandate adds to `RULES.armyLogistics`, at most
 * `legionMax` of them; `march`: what the MARCH card adds for the turn.
 */
export const LOGISTICS = {
  legion: 1,
  legionMax: 2,
  march: 9
} as const;

/** **The Artisan Pillar** (§31 A6, the design's numbers). */
export const ARTISAN = {
  /** Max HP added to every piece of the side while on; lost per Pillar lost. */
  health: 1,
  /** HP a piece regenerates each round while on, instead of 1. */
  regen: 2,
  /** Squares a Monarch's step reaches past its own. */
  reach: 2,
  /** Units off the dearest row of an upgrade's bill. */
  upgradeDiscount: 1
} as const;

/**
 * A **belt part** as a structure (§31 S2): 1 HP, not an obstruction — so it is
 * plowed, stood on and broken by the S1 model like any other plowable. Not a
 * `STRUCTURE_ROWS` kind: a part lives in the industry's belt maps, never in
 * `IndustryState.structures`, and joins the position's mirror as a belt.
 */
export const BELT_ROW: StructureRow = { obstruction: false, health: 1, bill: [stoneRow(0, 3)] };

/** The row for a kind; an unknown kind reads as a store (never thrown on). */
export const structureRow = (kind: number): StructureRow => STRUCTURE_ROWS[kind] ?? STRUCTURE_ROWS[0];

/** Material units a structure of `kind` holds (§33 I2): its row's, else 50. */
export const inventoryOf = (kind: number): number => structureRow(kind).inventory ?? RULES.structureInventory;

/**
 * The demand tiers an army can be given of its own, as a flat vector.
 *
 * **`RULES.demand` is the default and this is the per-side override**
 * (`Position.demand`, `demandOf`). The flow map decides where every unit of
 * material goes, and it was one table for everybody; giving each side its own
 * makes routing an *army trait* the tuner can search, which is deliberately an
 * invitation to emergent strategy — an army that would rather forge raw than
 * cook is a coherent thing to be, and nothing in the field could express it.
 *
 * A flat vector rather than an object because it is board state: it clones,
 * hashes and travels in the opening command, and eight numbers a side in one
 * `Float64Array` costs nothing next to an object per side. Read one slot at a
 * time (`demandOf`), so nothing allocates inside the routing sweep.
 *
 * **`store` is not here, and that is PLAN §14's most important tuning decision
 * being kept.** A store outranking an oven swallows every unit of ore and
 * halts the factory with a full warehouse; it is a strict last resort rather
 * than a preference, so it stays a rule nobody may search.
 */
export const DEMAND_SLOTS = [
  "refiner",
  "blueprint",
  "hungryFoundry",
  "foundry",
  "onward",
  "onwardCap",
  "stranded"
] as const;

export type DemandSlot = (typeof DEMAND_SLOTS)[number];

/** Slot indices, by name, so a reader never counts positions by hand. */
export const DEMAND: Record<DemandSlot, number> = Object.fromEntries(
  DEMAND_SLOTS.map((name, i) => [name, i])
) as Record<DemandSlot, number>;

/** The defaults, in slot order — what a side with no override of its own uses. */
export const DEMAND_DEFAULT: readonly number[] = DEMAND_SLOTS.map(
  (name) => RULES.demand[name]
);

/**
 * Who can work a heap, and how fast; 0 is not a harvester.
 *
 * The king harvests as well as refines, at three times a pawn's rate — it is
 * the piece you cannot lose, so putting it on a heap is a real commitment of
 * the one thing you have to keep safe, and it should be worth making. It only
 * starts refining once the heap under it is gone: the two jobs are sequential,
 * so a king on scrap is a mine and not an oven.
 */
export const HARVEST_RATE: Record<PieceType, number> = {
  [PAWN]: RULES.minerRate,
  [KNIGHT]: 0,
  [BISHOP]: 0,
  [ROOK]: 0,
  [QUEEN]: 0,
  // **The king does not dig.** It used to, at three times a pawn's rate, on
  // the argument that standing the piece you cannot lose on a heap commits the
  // one thing you have to keep safe and should be worth something. What that
  // produced in play was a king parked on scrap for the whole game: it was the
  // best digger on the board *and* the oven *and* the foundry, so the optimal
  // opening was to walk it to the nearest corpse and leave it there, which is
  // a strange thing to do with a king and made the pawns' one industrial job
  // pointless.
  //
  // It still *collects* — better than anything else, in fact — but by moving
  // rather than by grinding: a king lifts whatever it lands on, whole, through
  // the queen's own sweep (`vacuumAlong` in `applyMove`). Extraction is the
  // pawn's job; the king's is what happens to the metal afterwards.
  [KING]: 0
};

/**
 * How far a piece may slide in one move.
 *
 * On the classic board this is a no-op — the longest slide on eight files is
 * seven squares, so every published perft count is untouched, which is the
 * point: the cap is a rule for the board the run grows into, not a change to
 * chess. Once ground keeps opening outward an uncapped diagonal is a piece
 * that crosses the whole map in one move, and a board nobody can defend reads
 * as a board not worth building on.
 *
 * **Every piece is capped, the rook included.** It used to be the one
 * exception, on the argument that something has to answer "how do I cover
 * ground I have already taken". What that bought in reach it charged for in
 * the attack sweep: because a rook could attack from any distance, *no* query
 * about a square could ever stop early, so `isAttackedByEnemyOf` had to run
 * every rank and file to the edge of the board — on a board whose whole point
 * is that it grows. Measured, that query was 34% of the opponent's entire
 * thinking time, and the rank-and-file sweep is the half that scales with the
 * map. One number for every slider makes the bound a property of the *game*
 * rather than of the piece, and it is what lets every ray stop at ten.
 *
 * The rook is still the long-range piece: ten squares on a rank beats a
 * king's one and a knight's leap, and covering ground you already hold is now
 * a thing you do with lanes rather than with a single move across the map.
 */
export const SLIDER_RANGE: Record<PieceType, number> = {
  [PAWN]: 1,
  [KNIGHT]: 1,
  [BISHOP]: RULES.sliderRange,
  [ROOK]: RULES.sliderRange,
  [QUEEN]: RULES.sliderRange,
  [KING]: 1
};

/**
 * The furthest *any* piece can attack from, in squares.
 *
 * The one number every attack query needs: a ray that has gone this far
 * cannot find anything that reaches back, so it may stop. Derived from the
 * table rather than written down again — a sixth entry raised above the cap
 * would silently make check detection blind past it, which is the failure
 * mode the uncapped rook actually had in reverse.
 */
export const MAX_ATTACK_RANGE = Math.max(...Object.values(SLIDER_RANGE));
